import { ICoverageSummary } from '../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection-details.model';

// Human-readable labels for the document types and skip reasons on a sync's coverage
// summary. Unknown keys fall back to the raw key so a new backend reason still renders.
export const COVERAGE_DOCUMENT_LABELS: Record<string, string> = {
  BANK_TRANSACTION: 'bank transactions',
  PURCHASE: 'purchases',
  PAYMENT: 'payments',
  BILL: 'bills',
  CREDIT_NOTE: 'credit notes',
}

export const COVERAGE_SKIP_LABELS: Record<string, string> = {
  ALREADY_IMPORTED: 'already imported by an earlier sync',
  ALREADY_STAGED: 'already recorded from an earlier sync',
  ALREADY_DISCARDED: 'discarded by a reviewer earlier, so left out',
  DELETED_AFTER_IMPORT: 'imported earlier and their emissions since deleted. To bring them back, sync again from a date',
  SETTLES_IMPORTED_BILL: 'payments for bills that are imported separately',
  BILLS_EXCLUDED_BY_SETTINGS: 'bills excluded by your settings',
  EXCLUDED_BY_NOMINAL_MAPPING: 'excluded by your chart of accounts mapping',
  // Only a count is ever recorded for these: nothing else about them was stored.
  PRIVATE_BY_NOMINAL_MAPPING: "on codes you don't import, so they were not brought in",
}

/**
 * One paragraph saying what a sync fetched, imported, set aside and why. Shown above the
 * review table on the Financial page and in the connector pages' sync result, so the two
 * never describe the same sync differently.
 */
export const buildCoverageMessage = (coverage: ICoverageSummary): string => {
  const fetchedBreakdown = Object.entries(coverage.fetched || {})
    .filter(([, count]) => count > 0)
    .map(([type, count]) => `${count} ${COVERAGE_DOCUMENT_LABELS[type] || type.toLowerCase()}`)
    .join(', ')
  const parts = [
    `Last sync fetched ${coverage.fetchedCount} row${coverage.fetchedCount === 1 ? '' : 's'}`
    + (fetchedBreakdown ? ` (${fetchedBreakdown})` : '')
    + ` and imported ${coverage.importedCount}.`,
  ]
  // Staging has two causes and they need different sentences: one is a setting the user can
  // flip, the other is a mapping they set deliberately. Falls back to the old wording when
  // the breakdown is absent, which is any sync recorded before it existed.
  const stagedByReason = coverage.stagedExcluded || []
  const billsStaged = stagedByReason.find(entry => entry.reason === 'BILLS_EXCLUDED_BY_SETTINGS')?.count
    ?? (stagedByReason.length === 0 ? coverage.stagedExcludedCount : 0)
  const nominalStaged = stagedByReason.find(entry => entry.reason === 'EXCLUDED_BY_NOMINAL_MAPPING')?.count ?? 0
  if (billsStaged > 0) {
    parts.push(`${billsStaged} bill${billsStaged === 1 ? ' was' : 's were'} found but not imported: bill import is off for your organisation. Turn on "Import Supplier Bills from Accounting Syncs" in Settings to include them.`)
  }
  if (nominalStaged > 0) {
    parts.push(`${nominalStaged} line${nominalStaged === 1 ? ' was' : 's were'} excluded by your chart of accounts mapping. They stay visible in the Discarded tab with the code that excluded them.`)
  }
  if (coverage.nominal && coverage.nominal.mixed > 0) {
    parts.push(`${coverage.nominal.mixed} document${coverage.nominal.mixed === 1 ? '' : 's'} post to more than one nominal code, so ${coverage.nominal.mixed === 1 ? 'it kept its' : 'they kept their'} supplier classification for you to check.`)
  }
  const restored = coverage.restoredCount ?? 0
  if (restored > 0) {
    parts.push(`${restored} of ${restored === 1 ? 'them was' : 'them were'} imported before and had ${restored === 1 ? 'its' : 'their'} emissions deleted, so ${restored === 1 ? 'it is' : 'they are'} back for review.`)
  }
  for (const skip of coverage.skipped || []) {
    parts.push(`${skip.count} skipped: ${COVERAGE_SKIP_LABELS[skip.reason] || skip.reason}.`)
  }
  const withheldPartial = coverage.nominal?.withheldPartial ?? 0
  if (withheldPartial > 0) {
    parts.push(`${withheldPartial} ${withheldPartial === 1 ? 'document was' : 'documents were'} imported without the part on codes you don't import.`)
  }
  if (coverage.duplicateCount > 0) {
    parts.push(`${coverage.duplicateCount} imported line${coverage.duplicateCount === 1 ? ' is' : 's are'} flagged as possible duplicates for review.`)
  }
  if (coverage.truncated) {
    parts.push(coverage.truncatedDetail || 'The date window was too large to fetch completely; narrow it and sync again.')
  }
  return parts.join(' ')
}
